<template>
    <div class="flex items-start gap-[35px]">
        <div class="flex-1 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            <div
                class="wizard-topbar flex items-center justify-between gap-6 border-b border-gray-200 px-[21px] py-[21px]"
            >
                <Stepper
                    class="wizard-stepper"
                    :steps="steps"
                    :current-step="currentStep"
                />

                <div class="wizard-actions flex items-center gap-3">
                    <button
                        v-if="canGoBack"
                        type="button"
                        class="cursor-pointer inline-flex h-[31.5px] items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        @click="goBack"
                    >
                        <ArrowLeft class="h-4 w-4" />
                        {{ $t("vue.back") }}
                    </button>

                    <button
                        type="button"
                        class="cursor-pointer btn-hover-dim inline-flex h-[31.5px] items-center gap-2 rounded-md px-3 text-[11px] font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="primaryBtnClass"
                        :disabled="primaryDisabled"
                        @click="goNextOrFinish"
                    >
                        <template v-if="currentStep === 1">
                            {{ t("vue.domain_wizard_add_domain") }}
                            <ArrowRight class="h-4 w-4" />
                        </template>

                        <template v-else-if="currentStep === 2">
                            {{ $t("vue.continue_to_review") }}
                            <ArrowRight class="h-4 w-4" />
                        </template>

                        <template v-else> {{ t("vue.finish") }}</template>
                    </button>
                </div>
            </div>

            <div class="px-8 py-10">
                <!-- STEP 1 - ADD YOUR DOMAIN -->
                <section v-if="currentStep === 1" class="mx-auto max-w-3xl">
                    <div class="text-left">
                        <h2 class="text-xl font-semibold text-gray-900">
                            {{ $t("vue.enter_your_domain") }}
                        </h2>
                        <p class="mt-2 text-sm text-gray-600">
                            {{
                                $t("vue.enter_your_domain_to_start_monitoring")
                            }}
                        </p>
                    </div>

                    <div
                        class="mt-8 rounded-[12.8px] border border-gray-200 bg-gray-50 p-[14px]"
                    >
                        <input
                            v-model.trim="domain"
                            type="text"
                            inputmode="url"
                            :placeholder="t('vue.my_beatiful_domain_co')"
                            class="mt-2 w-full rounded-[7px] border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            @blur="domainTouched = true"
                            @keydown.enter.prevent="goNextOrFinish"
                        />
                    </div>

                    <div
                        v-if="
                            (domainTouched || step1Error) &&
                            (step1Error || !isValidDomain(domain))
                        "
                        class="mt-4 flex items-start gap-3 rounded-[12.8px] border border-red-300 bg-red-100 px-5 py-4 text-red-600"
                    >
                        <svg
                            class="mt-0.5 h-5 w-5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path
                                d="M10.3 3.6 1.8 18.2A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.8L13.7 3.6a2 2 0 0 0-3.4 0Z"
                            />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>

                        <div class="text-sm font-medium leading-6">
                            {{ step1Error || $t("vue.invalid_domain_format") }}
                        </div>
                    </div>
                </section>

                <!-- STEP 2 - CONFIGURE DNS RECORD -->
                <section
                    v-else-if="currentStep === 2"
                    class="mx-auto max-w-3xl"
                >
                    <div class="text-left">
                        <h2 class="text-2xl font-semibold text-gray-900">
                            {{ $t("vue.configure_dmarc_and_add_dns_record") }}
                        </h2>
                        <p class="mt-2 text-sm text-gray-500">
                            {{ $t("vue.set_your_dmarc_policy") }}
                        </p>
                    </div>

                    <div
                        class="mt-10 rounded-[12.8px] border border-gray-200 bg-gray-50 p-6"
                    >
                        <div class="flex items-start gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-4 w-4 text-green-500 mt-[1px]"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>

                            <div>
                                <p class="text-sm font-medium text-gray-700">
                                    {{ $t("vue.domain_successfully_created") }}
                                </p>
                                <span class="mt-1 inline-block -ml-[23px]">
                                    <p
                                        class="text-lg font-semibold text-gray-900"
                                    >
                                        {{ domain }}
                                    </p>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="mt-7 grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                            <label
                                class="block text-sm font-semibold text-gray-800"
                            >
                                {{ $t("vue.dmarc_policy") }}
                            </label>

                            <select
                                v-model="dmarcPolicy"
                                class="mt-2 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            >
                                <option value="none">
                                    {{ t("vue.none") }} ({{
                                        t("vue.monitor_only")
                                    }})
                                </option>
                                <option value="quarantine">
                                    {{ t("vue.quarantine") }}
                                </option>
                                <option value="reject">
                                    {{ t("vue.reject") }}
                                </option>
                            </select>

                            <p class="mt-2 text-xs text-gray-400">
                                {{
                                    $t(
                                        "vue.start_with_none_settings_with_no_affecting",
                                    )
                                }}
                            </p>
                        </div>

                        <div>
                            <label
                                class="block text-sm font-semibold text-gray-800"
                            >
                                {{ $t("vue.dmarc_policy_app_percentage") }}
                            </label>

                            <input
                                v-model.number="policyPct"
                                type="number"
                                min="0"
                                max="100"
                                class="mt-2 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />

                            <p class="mt-2 text-xs text-gray-400">
                                {{
                                    $t(
                                        "vue.start_one_hundred_percent_monitoring",
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <div
                        class="mt-8 rounded-[12.8px] bg-[#0B1220] px-8 py-7 !text-white"
                    >
                        <div
                            class="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-20"
                        >
                            <div>
                                <p class="text-xs font-medium !text-white/60">
                                    {{ t("vue.domain_wizard_record_type") }}
                                </p>
                                <p
                                    class="mt-1 text-sm font-semibold !text-white"
                                >
                                    TXT
                                </p>
                            </div>

                            <div>
                                <p class="text-xs font-medium !text-white/60">
                                    {{ t("vue.domain_wizard_host_name") }}
                                </p>
                                <p
                                    class="mt-1 text-sm font-semibold !text-white"
                                >
                                    _dmarc
                                </p>
                            </div>

                            <div class="md:col-span-2">
                                <p class="text-xs font-medium !text-white/60">
                                    {{ t("vue.domain_wizard_value") }}
                                </p>
                                <p
                                    class="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed !text-white"
                                >
                                    {{ txtRecordPreview }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-6 rounded-[6.75px] border border-dashed border-gray-300 bg-[#F9FAFB]"
                    >
                        <button
                            type="button"
                            class="flex w-full items-center justify-between px-[14px] py-[14px]"
                            @click="toggleEmailInstructions"
                        >
                            <div class="flex items-center gap-2">
                                <Mail class="h-4 w-4 text-blue-600" />
                                <div
                                    class="text-[14px] font-medium leading-[142%]"
                                    style="color: #161e2e"
                                >
                                    {{
                                        t(
                                            "vue.domain_wizard_email_instructions",
                                        )
                                    }}
                                </div>
                            </div>

                            <span
                                class="text-[10.5px] font-medium leading-[142%] text-gray-500"
                            >
                                {{
                                    emailInstructionsOpen
                                        ? t("vue.cancel")
                                        : t("vue.show_details")
                                }}
                            </span>
                        </button>

                        <div
                            v-if="emailInstructionsOpen"
                            class="border-t border-gray-200 px-[14px] pb-[14px]"
                        >
                            <div class="mt-[15.5px] flex flex-col gap-[15.5px]">
                                <div>
                                    <label
                                        class="block text-[12px] font-medium leading-[140%]"
                                        style="color: #000000"
                                    >
                                        {{
                                            t("vue.domain_wizard_send_to_email")
                                        }}
                                    </label>

                                    <input
                                        v-model.trim="emailTo"
                                        type="email"
                                        :placeholder="
                                            t(
                                                'vue.domain_wizard_email_placeholder',
                                            )
                                        "
                                        class="mt-2 h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:placeholder-transparent"
                                        @blur="emailToTouched = true"
                                    />

                                    <p
                                        v-if="emailToTouched && emailToError"
                                        class="mt-2 text-xs text-red-600"
                                    >
                                        {{ emailToError }}
                                    </p>
                                </div>

                                <div>
                                    <label
                                        class="block text-[12px] font-medium leading-[140%]"
                                        style="color: #000000"
                                    >
                                        {{
                                            t(
                                                "vue.domain_wizard_message_optional",
                                            )
                                        }}
                                    </label>

                                    <textarea
                                        v-model="emailMessage"
                                        rows="9"
                                        class="mt-2 w-full resize-y rounded-md border border-gray-200 bg-white p-3 text-[12px] leading-[140%] text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />

                                    <p
                                        v-if="emailStatusText"
                                        class="mt-2 text-xs"
                                        :class="emailStatusClass"
                                    >
                                        {{ emailStatusText }}
                                    </p>
                                </div>

                                <div class="flex justify-center">
                                    <button
                                        type="button"
                                        class="cursor-pointer inline-flex h-[31.5px] items-center justify-center rounded-md bg-[#088BEA] px-4 text-[11px] font-medium text-white disabled:opacity-50"
                                        :disabled="
                                            emailSending || !canSendEmail
                                        "
                                        @click="sendEmail"
                                    >
                                        {{
                                            emailSending
                                                ? t("vue.domain_wizard_sending")
                                                : t(
                                                      "vue.domain_wizard_send_email",
                                                  )
                                        }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- STEP 3 - REVIEW & CONFIRM -->
                <section v-else class="mx-auto max-w-3xl">
                    <div class="text-left">
                        <h2 class="text-2xl font-semibold text-gray-900">
                            {{ $t("vue.review_and_confirm") }}
                        </h2>
                        <p class="mt-2 text-sm text-gray-500">
                            {{
                                $t("vue.review_dmarc_conf_before_go_to_reports")
                            }}
                        </p>
                    </div>

                    <div class="mt-8 space-y-6">
                        <div
                            class="mt-10 rounded-[12.8px] border border-gray-200 bg-gray-50 p-6"
                        >
                            <div class="flex items-start gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="h-4 w-4 text-green-500 mt-[1px]"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>

                                <div>
                                    <p
                                        class="text-sm font-medium text-gray-700"
                                    >
                                        {{ $t("vue.domain_name") }}
                                    </p>
                                    <span class="mt-1 inline-block -ml-[23px]">
                                        <p
                                            class="text-lg font-semibold text-gray-900"
                                        >
                                            {{ domain }}
                                        </p>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            class="rounded-[12.8px] border border-gray-200 bg-white px-6 py-5"
                        >
                            <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                                <div>
                                    <p
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        {{ $t("vue.created_at") }}
                                    </p>
                                    <p
                                        class="text-sm text-gray-500 policy-badge mt-2"
                                    >
                                        {{ createdAt }}
                                    </p>
                                </div>

                                <div>
                                    <p
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        {{ $t("vue.status") }}
                                    </p>
                                    <span
                                        class="policy-badge mt-2"
                                        :class="
                                            dnsValidated
                                                ? 'badge-active'
                                                : 'badge-inactive'
                                        "
                                    >
                                        {{
                                            dnsValidated
                                                ? t("vue.active")
                                                : t("vue.inactive")
                                        }}
                                    </span>
                                </div>

                                <div>
                                    <p
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        {{ $t("vue.dmarc_policy") }}
                                    </p>
                                    <div class="text-gray-500 text-sm mt-2">
                                        <span
                                            v-if="existingDomain?.dmarcRecord"
                                            class="policy-badge capitalize"
                                            :class="{
                                                'policy-reject':
                                                    dmarcPolicyFromDb ===
                                                    'reject',
                                                'policy-quarantine':
                                                    dmarcPolicyFromDb ===
                                                    'quarantine',
                                                'policy-none':
                                                    dmarcPolicyFromDb !==
                                                        'reject' &&
                                                    dmarcPolicyFromDb !==
                                                        'quarantine',
                                            }"
                                        >
                                            {{ dmarcPolicyFromDb }}
                                        </span>

                                        <span
                                            v-else
                                            class="policy-badge policy-none capitalize"
                                        >
                                            {{ t("vue.none") }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="rounded-[12.8px] border px-6 py-5"
                            :class="
                                dnsValidated
                                    ? 'border-green-200 bg-green-50'
                                    : 'border-yellow-200 bg-yellow-50'
                            "
                        >
                            <div class="flex items-start gap-3">
                                <span
                                    class="mt-2 inline-block h-2 w-2 rounded-full"
                                    :class="
                                        dnsValidated
                                            ? 'bg-green-500'
                                            : 'bg-yellow-500'
                                    "
                                    aria-hidden="true"
                                />
                                <div class="flex-1">
                                    <p
                                        class="text-sm font-semibold"
                                        :class="
                                            dnsValidated
                                                ? 'text-green-900'
                                                : 'text-yellow-900'
                                        "
                                    >
                                        {{
                                            dnsValidated
                                                ? ""
                                                : t("vue.waiting_for_records")
                                        }}
                                    </p>

                                    <p
                                        class="max-w-[620px] text-sm leading-6"
                                        :class="
                                            dnsValidated
                                                ? 'text-green-800'
                                                : 'text-yellow-800'
                                        "
                                    >
                                        <template v-if="dnsValidated">
                                            {{
                                                $t(
                                                    "vue.dmarc_dns_validated_txt_instruction",
                                                )
                                            }}
                                        </template>
                                        <template v-else>
                                            {{
                                                $t("vue.dmarc_txt_instruction")
                                            }}
                                        </template>
                                    </p>

                                    <button
                                        v-if="!dnsValidated"
                                        type="button"
                                        class="cursor-pointer mt-4 inline-flex h-[31.5px] items-center rounded-md bg-[#088BEA] px-4 text-sm font-medium text-white disabled:opacity-50"
                                        :disabled="dnsRefreshBusy"
                                        @click="refreshDns"
                                    >
                                        {{
                                            dnsRefreshBusy
                                                ? t("vue.checking_progress")
                                                : t("vue.refresh_dns_record")
                                        }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- RIGHT SIDEBAR -->
        <aside class="hidden w-[340px] shrink-0 lg:block">
            <div class="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
                <div
                    class="flex items-start gap-2 border-b border-gray-200 px-[21px] py-[21px] h-[74.5px]"
                >
                    <Lightbulb class="mt-[2px] h-4 w-4 text-gray-800" />
                    <div>
                        <div
                            class="text-[14px] font-medium leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ t("vue.domain_wizard_setup_title") }}
                        </div>
                        <div
                            class="text-[10px] font-normal leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ t("vue.domain_wizard_setup_subtitle") }}
                        </div>
                    </div>
                </div>

                <div class="px-6 py-5">
                    <div class="space-y-3">
                        <div
                            v-for="(it, idx) in sidebarItems"
                            :key="idx"
                            class="flex items-start gap-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-[11px] w-[11px] shrink-0 text-[#00C951]"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>
                            <div
                                class="text-[12.25px] font-normal leading-[142%]"
                                style="color: #4b5563"
                            >
                                {{ it }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="h-px bg-gray-200" />

                <div class="px-6 py-6 text-center">
                    <div
                        class="mx-auto h-[62px] w-[62px] rounded-full bg-gray-100 bg-cover bg-center bg-no-repeat"
                        style="
                            background-image: url(&quot;https://jakubplas.cz/_assets/v11/2b4367e8fee766c89b4f3149df03fd1daf896b48.png&quot;);
                        "
                    ></div>
                    <div
                        class="mt-3 text-[14px] font-medium leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_need_help") }}
                    </div>

                    <div
                        class="mx-auto mt-2 max-w-[280px] text-[12.25px] font-normal leading-[142%]"
                        style="color: #4b5563"
                    >
                        {{ t("vue.domain_wizard_help_text") }}
                    </div>

                    <a :href="demoCallUrl" target="_blank">
                        <AtomicButton
                            class="mt-2 h-[31.5px] px-4 text-[11px] hover:bg-black hover:text-white transition"
                            color="colorless"
                        >
                            {{ $t("vue.schedule_a_call") }}
                        </AtomicButton>
                    </a>
                </div>
            </div>
        </aside>
    </div>

    <!-- mobile sidebar -->
    <div class="mt-8 lg:hidden">
        <div class="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            <div
                class="flex items-start gap-2 border-b border-gray-200 px-[21px] py-[21px]"
            >
                <Lightbulb class="mt-[2px] h-4 w-4 text-gray-800" />
                <div>
                    <div
                        class="text-[14px] font-medium leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_setup_title") }}
                    </div>
                    <div
                        class="text-[10px] font-normal leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_setup_subtitle") }}
                    </div>
                </div>
            </div>

            <div class="px-6 py-5">
                <div class="space-y-3">
                    <div
                        v-for="(it, idx) in sidebarItems"
                        :key="idx"
                        class="flex items-start gap-2"
                    >
                        <CheckCircle2
                            class="mt-[2px] h-3 w-3 text-green-500/80"
                        />
                        <div
                            class="text-[12.25px] font-normal leading-[142%]"
                            style="color: #4b5563"
                        >
                            {{ it }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="h-px bg-gray-200" />

            <div class="px-6 py-6 text-center">
                <div
                    class="mx-auto flex h-[62px] w-[62px] items-center justify-center rounded-full bg-gray-100"
                >
                    <UserRound class="h-7 w-7 text-gray-700" />
                </div>

                <div
                    class="mt-3 text-[14px] font-medium leading-[142%]"
                    style="color: #161e2e"
                >
                    {{ t("vue.domain_wizard_need_help") }}
                </div>

                <div
                    class="mx-auto mt-2 max-w-[280px] text-[12.25px] font-normal leading-[142%]"
                    style="color: #4b5563"
                >
                    {{ t("vue.domain_wizard_help_text") }}
                </div>

                <a
                    :href="demoCallUrl"
                    target="_blank"
                    class="mt-4 inline-flex h-[31.5px] w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-[11px] font-medium text-gray-800 transition hover:bg-black hover:text-white"
                >
                    {{ t("vue.schedule_a_call") }}
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ArrowLeft,
    ArrowRight,
    Lightbulb,
    CheckCircle2,
    UserRound,
    Mail,
} from "lucide-vue-next";
import route from "ziggy-js";
import { router, usePage } from "@inertiajs/vue3";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import axios from "axios";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();
const page = usePage();
const demoCallUrl = computed(() =>
    (page.props as { locale?: string }).locale === "cs"
        ? "https://dmarceye.com/cs/democall"
        : "https://dmarceye.com/talk-to-sales",
);

type Step = { key: number; title: string };

const domain = ref("");
const loading = ref(false);
const domainTouched = ref(false);
const step1Error = ref("");
const txtRecordFromServer = ref<string | null>(null);

const existingDomain = ref<DomainDto | null>(null);

type DomainDto = {
    domain: string;
    account?: string;
    active?: boolean;
    dmarcRecord?: string | null;
    created_at?: string | null;
};
const props = defineProps<{
    domainsCount?: number;
    domains?: DomainDto[];
    uuid: string;
}>();

const Stepper = defineComponent({
    name: "Stepper",
    components: { CheckCircle2 },
    props: {
        steps: {
            type: Array as () => Array<{ key: number; title: string }>,
            required: true,
        },
        currentStep: { type: Number as () => 1 | 2 | 3, required: true },
    },
    setup(props) {
        const statusOf = (key: number) =>
            key < props.currentStep
                ? "done"
                : key === props.currentStep
                  ? "active"
                  : "todo";

        const circleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "done") return "bg-[#04C951] text-white";
            if (s === "active") return "bg-[#088BEA] text-white";
            return "bg-gray-200 text-gray-500";
        };

        const titleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "todo") return "text-gray-400";
            return "text-gray-800";
        };

        const lineClass = (key: number) =>
            key < props.currentStep ? "bg-gray-300" : "bg-gray-200";

        const isDone = (key: number) => statusOf(key) === "done";

        return { circleClass, titleClass, lineClass, isDone };
    },
    template: `
        <div class="flex items-center gap-4">
            <div v-for="(s, idx) in steps" :key="s.key" class="flex items-center">
                <div class="flex items-center gap-3">
                    <div class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold"
                         :class="circleClass(s.key)">
                        <CheckCircle2 v-if="isDone(s.key)" class="h-5 w-5"/>
                        <span v-else>{{ s.key }}</span>
                    </div>
                    <div class="text-sm font-medium whitespace-nowrap" :class="titleClass(s.key)">
                        {{ s.title }}
                    </div>
                </div>
                <div v-if="idx !== steps.length - 1" class="mx-4 h-px w-16" :class="lineClass(s.key)"></div>
            </div>
        </div>
    `,
});

const steps: Step[] = [
    { key: 1, title: t("vue.add_your_domain") },
    { key: 2, title: t("vue.configure_dns_record") },
    { key: 3, title: t("vue.review_and_confirm") },
];

const currentStep = ref<1 | 2 | 3>(1);
const canGoBack = computed(() => currentStep.value === 3);

const createdAt = ref<string>(new Date().toLocaleDateString());

const dmarcPolicy = ref<"none" | "quarantine" | "reject">("none");
const policyPct = ref<number>(100);

const ruaLocalPart = computed(() => {
    const v = String(props.uuid ?? "").trim();
    return v.length ? v : "reports";
});

const ruaMailbox = computed(() => `${ruaLocalPart.value}@rua.dmarceye.com`);

const dnsValidated = ref(false);
const dnsRefreshBusy = ref(false);

const sidebarItems = computed<string[]>(() => {
    if (currentStep.value === 1) {
        return [
            t("vue.domain_wizard_sidebar_step1_item1"),
            t("vue.domain_wizard_sidebar_step1_item2"),
        ];
    }

    if (currentStep.value === 2) {
        return [
            t("vue.domain_wizard_sidebar_step2_item1"),
            t("vue.domain_wizard_sidebar_step2_item2"),
            t("vue.domain_wizard_sidebar_step2_item3"),
            t("vue.domain_wizard_sidebar_step2_item4"),
            t("vue.domain_wizard_sidebar_step2_item5"),
        ];
    }

    return [
        t("vue.domain_wizard_sidebar_step3_item1"),
        t("vue.domain_wizard_sidebar_step3_item2"),
        t("vue.domain_wizard_sidebar_step3_item3"),
    ];
});

const canSubmitStep1 = computed(
    () => isValidDomain(domain.value) && !loading.value,
);

const primaryDisabled = computed(() => {
    if (currentStep.value === 1) return !canSubmitStep1.value;
    if (currentStep.value === 2) return !domain.value;
    return loading.value;
});

const primaryBtnClass = computed(() => {
    if (currentStep.value === 3)
        return primaryDisabled.value ? "bg-green-300" : "bg-[#04C951]";
    return primaryDisabled.value ? "bg-gray-300" : "bg-[#088BEA]";
});

function goBack() {
    if (currentStep.value === 3) currentStep.value = 2;
}

function goNextOrFinish() {
    if (currentStep.value === 1) {
        addDomain();
        return;
    }

    if (currentStep.value === 2) {
        currentStep.value = 3;
        return;
    }

    router.visit(route("domains.index"));
}

type DnsCheckResponse = {
    success: boolean;
    result?: {
        active: boolean;
        dmarc_record?: string | null;
        dmarc_policy?: "none" | "quarantine" | "reject" | string;
    };
    checked_at?: string;
};

async function refreshDns() {
    if (!domain.value || dnsRefreshBusy.value) return;

    dnsRefreshBusy.value = true;

    try {
        const response = await axios.get<DnsCheckResponse>(
            route("domains.check-dns", { domain: domain.value }),
            { headers: { Accept: "application/json" } },
        );

        const data = response.data;
        if (!data?.success || !data.result) return;

        dnsValidated.value = Boolean(data.result.active);

        txtRecordFromServer.value = data.result.dmarc_record ?? null;

        existingDomain.value = {
            ...(existingDomain.value ?? { domain: domain.value }),
            domain: domain.value,
            active: data.result.active,
            dmarcRecord: data.result.dmarc_record ?? null,
        };

        applyRecordDefaults(txtRecordFromServer.value);

        if (dnsValidated.value) currentStep.value = 3;
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsRefreshBusy.value = false;
    }
}

function clampInt(n: number, min: number, max: number) {
    const x = Number.isFinite(n) ? Math.round(n) : min;
    return Math.min(max, Math.max(min, x));
}

function isValidDomain(input: string): boolean {
    const v = input.trim().toLowerCase();
    return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
        v,
    );
}

function mapDomainError(err: unknown): string {
    const e = err as Record<string, any> | undefined;
    const msgRaw = e?.domain ?? e?.errors?.domain;

    const msg = Array.isArray(msgRaw)
        ? String(msgRaw[0] ?? "")
        : String(msgRaw ?? "");
    const lower = msg.toLowerCase();

    if (
        lower.includes("taken") ||
        lower.includes("exists") ||
        lower.includes("already")
    ) {
        return t("vue.domain_wizard_domain_exists");
    }

    return t("vue.domain_wizard_domain_invalid");
}

function addDomain() {
    domainTouched.value = true;
    step1Error.value = "";

    if (!isValidDomain(domain.value)) {
        step1Error.value = t("vue.domain_wizard_domain_invalid");
        return;
    }

    router.post(
        route("domains.store"),
        { domain: domain.value },
        {
            onBefore: () => {
                loading.value = true;
            },
            onSuccess: (page: any) => {
                const d = domain.value;
                const recordToAdd =
                    page?.props?.inactiveDomains?.[d]?.recordToAdd;

                txtRecordFromServer.value =
                    typeof recordToAdd === "string" ? recordToAdd : null;
                applyRecordDefaults(txtRecordFromServer.value);

                createdAt.value = new Date().toLocaleDateString();
                currentStep.value = 2;
            },
            onError: (err) => {
                step1Error.value = mapDomainError(err);
            },
            onFinish: () => {
                loading.value = false;
            },
            preserveScroll: true,
        },
    );
}

onMounted(() => {
    const count = props.domainsCount ?? 0;
    const list = props.domains ?? [];

    if (count <= 0 || list.length === 0) {
        currentStep.value = 1;
        existingDomain.value = null;
        dnsValidated.value = false;

        recordParts.value.rua = `mailto:${ruaMailbox.value}`;
        return;
    }

    const first = list[0];
    existingDomain.value = first;

    domain.value = first.domain ?? "";
    createdAt.value = first.created_at
        ? new Date(first.created_at).toLocaleDateString()
        : createdAt.value;

    dnsValidated.value = Boolean(first.active);

    txtRecordFromServer.value = first.dmarcRecord ?? null;

    applyRecordDefaults(first.dmarcRecord ?? null);

    if (!first.dmarcRecord) {
        recordParts.value.rua = `mailto:${ruaMailbox.value}`;
    }

    currentStep.value = dnsValidated.value ? 3 : 2;
});

type DmarcParts = {
    rua: string;
    fo: string;
    ri: string;
};

const recordParts = ref<DmarcParts>({
    rua: `mailto:${ruaMailbox.value}`,
    fo: "1",
    ri: "86400",
});

function pickTag(record: string, tag: string): string | null {
    const re = new RegExp(`(?:^|;\\s*)${tag}\\s*=\\s*([^;]+)`, "i");
    const m = record.match(re);
    return m?.[1]?.trim() ?? null;
}

function applyRecordDefaults(record: string | null | undefined) {
    if (!record) return;

    const p = pickTag(record, "p")?.toLowerCase();
    if (p === "none" || p === "quarantine" || p === "reject") {
        dmarcPolicy.value = p;
    }

    const pctRaw = pickTag(record, "pct");
    const pct = pctRaw ? parseInt(pctRaw, 10) : NaN;
    if (Number.isFinite(pct)) policyPct.value = clampInt(pct, 0, 100);

    const rua = pickTag(record, "rua");
    if (rua) recordParts.value.rua = rua;

    const fo = pickTag(record, "fo");
    if (fo) recordParts.value.fo = fo;

    const ri = pickTag(record, "ri");
    if (ri) recordParts.value.ri = ri;
}

const txtRecordPreview = computed(() => {
    const pct = clampInt(policyPct.value, 0, 100);
    const rua = recordParts.value.rua || `mailto:${ruaMailbox.value}`;
    return `v=DMARC1; p=${dmarcPolicy.value}; pct=${pct}; rua=${rua}; fo=${recordParts.value.fo}; ri=${recordParts.value.ri}`;
});

function parseDmarcPolicyFromRecord(
    record: string | null | undefined,
): "none" | "quarantine" | "reject" {
    if (!record) return "none";
    const m = record.match(/(?:^|;\s*)p\s*=\s*(none|quarantine|reject)\b/i);
    const p = (m?.[1] ?? "none").toLowerCase();
    if (p === "reject" || p === "quarantine") return p;
    return "none";
}

const dmarcPolicyFromDb = computed<"none" | "quarantine" | "reject">(() => {
    const pDb = parseDmarcPolicyFromRecord(existingDomain.value?.dmarcRecord);
    return pDb ?? "none";
});

const mailDirty = ref(false);
const developerMailBodyDraft = ref("");

const buildDeveloperMailBody = () => {
    const d = domain.value || t("vue.domain_wizard_example_domain");
    return t("vue.domain_wizard_developer_email_body", {
        domain: d,
        txtRecord: txtRecordPreview.value,
    });
};

watch(
    [domain, txtRecordPreview],
    () => {
        if (!mailDirty.value)
            developerMailBodyDraft.value = buildDeveloperMailBody();
    },
    { immediate: true },
);

watch(developerMailBodyDraft, () => {
    mailDirty.value = true;
});

const emailTo = ref("");
const emailToTouched = ref(false);
const emailMessage = ref("");
const emailSending = ref(false);
const emailSent = ref(false);
const emailError = ref<string | null>(null);
const emailInstructionsOpen = ref(false);

const emailMessageDirty = ref(false);

const emailToError = computed(() => {
    if (!emailTo.value.trim()) return t("vue.domain_wizard_email_required");
    const v = emailTo.value.trim().toLowerCase();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return ok ? "" : t("vue.domain_wizard_email_invalid");
});

const canSendEmail = computed(() => !emailToError.value);

const emailStatusText = computed(() => {
    if (emailError.value) return emailError.value;
    if (emailSent.value) return t("vue.domain_wizard_email_queued");
    return "";
});

const emailStatusClass = computed(() => {
    if (emailError.value) return "text-red-600";
    if (emailSent.value) return "text-green-700";
    return "text-gray-500";
});

const buildEmailMessage = () => {
    const d = domain.value || t("vue.domain_wizard_example_domain");
    return t("vue.domain_wizard_email_body", {
        domain: d,
        txtRecord: txtRecordPreview.value,
    });
};

function resetEmailForm() {
    emailError.value = null;
    emailSent.value = false;
    emailToTouched.value = false;

    emailTo.value = "";
    emailMessageDirty.value = false;
    emailMessage.value = buildEmailMessage();
}

function toggleEmailInstructions() {
    emailInstructionsOpen.value = !emailInstructionsOpen.value;
    if (emailInstructionsOpen.value) {
        resetEmailForm();
    }
}

watch(
    [domain, txtRecordPreview],
    () => {
        if (!emailMessageDirty.value) emailMessage.value = buildEmailMessage();
    },
    { immediate: true },
);

watch(emailMessage, () => {
    emailMessageDirty.value = true;
});

async function sendEmail() {
    emailError.value = null;
    emailSent.value = false;

    if (!canSendEmail.value) {
        emailToTouched.value = true;
        return;
    }

    emailSending.value = true;

    try {
        const payload = {
            to: emailTo.value.trim(),
            message: emailMessage.value,
        };

        const response = await axios.post(
            route("domainGuide.sendWizardSetEmail"),
            payload,
            {
                headers: { Accept: "application/json" },
            },
        );

        const { status, message } = response.data ?? {};

        if (status === "error") {
            create({
                type: "error",
                title: message || t("vue.domain_wizard_email_send_failed"),
            });
            emailError.value =
                message || t("vue.domain_wizard_email_send_failed");
            console.log(message);
            return;
        }

        if (status === "ok") {
            create({
                type: "success",
                title: t("vue.domain_wizard_email_sent"),
            }); // bez message
            emailSent.value = true;
        }

        emailSent.value = true;
    } catch (e) {
        emailError.value = t("vue.domain_wizard_email_send_failed");
    } finally {
        emailSending.value = false;
    }
}
</script>

<style scoped>
.btn-hover-dim {
    transition:
        filter 150ms ease,
        transform 150ms ease;
}

.btn-hover-dim:hover:not(:disabled) {
    filter: brightness(0.92);
}

.btn-hover-dim:active:not(:disabled) {
    filter: brightness(0.88);
}

@media (max-width: 1000px) {
    .wizard-topbar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .wizard-actions {
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 10px;
    }
}

@media (max-width: 760px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-16) {
        width: 28px;
    }
}

@media (max-width: 520px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-16) {
        width: 28px;
    }
}
</style>
